import moment from "moment-timezone"
import { breakdownLabel, logTime, placeName, quantityLabel } from "pages/EventsManagement/components/Tabs/Logistics/labels"

// The warehouse at Câmpina keeps Romania's days and times.
const HOME_ZONE = "Europe/Bucharest"
// As the API: the longest reason or document field, the largest quantity.
export const TEXT_LIMIT = 500
export const FIELD_LIMIT = 200
const QUANTITY_LIMIT = 1000000

export const MOVEMENT_TYPES = [
  ["initialInventory", "Initial inventory", "soft-primary"], ["receipt", "Receipt", "soft-success"], ["manualIssue", "Manual issue", "soft-warning"],
  ["inventory", "Inventory", "soft-primary"], ["reversal", "Reversal", "soft-danger"], ["shipment", "Shipment", "soft-info"],
  ["shipmentCorrection", "Shipment correction", "soft-info"], ["shipmentCancellation", "Shipment cancelled", "soft-info"], ["return", "Return", "soft-success"],
  ["returnCorrection", "Return correction", "soft-warning"],
]
// The movements a shipment writes, changed only through the shipment, on its event's Logistics tab.
export const SHIPMENT_MOVEMENTS = ["shipment", "shipmentCorrection", "shipmentCancellation", "return", "returnCorrection"]
const typeOf = type => MOVEMENT_TYPES.find(([value]) => value === type)
export const typeLabel = type => typeOf(type)?.[1] || type
export const typeColor = type => typeOf(type)?.[2] || "soft-secondary"
// What a reversal names: "the receipt of 21 Sep 2026, 13:40".
const reversedNames = { receipt: "receipt", manualIssue: "manual issue", inventory: "inventory count" }

export const DESTINATIONS = [["tenis10", "Tenis10"], ["ridersClub", "Riders Club"], ["sponsor", "Sponsor"], ["loss", "Loss"]]
const destinationLabel = value => DESTINATIONS.find(([key]) => key === value)?.[1] || value

// The ledger's periods, by Romania's days, the last 30 days first.
export const PERIODS = [["30", "Last 30 days"], ["90", "Last 90 days"], ["365", "Last 12 months"], ["all", "All time"]]
export const todayIn = now => moment(now()).tz(HOME_ZONE).format("YYYY-MM-DD")
export const periodStart = (period, now) => period === "all" ? null
  : moment.tz(todayIn(now), HOME_ZONE).subtract(Number(period) - 1, "days").format("YYYY-MM-DD")

// "21 Sep 2026, 14:02", as the logistics logs write a time, and "1 Sep 2026", in Romania's time.
export const timeLabel = value => logTime(value, HOME_ZONE)
export const dayLabel = value => moment(value).tz(HOME_ZONE).format("D MMM YYYY")

// "+500", "−20", "0": a movement's signed quantity; "−2", "18": a balance.
export const signed = quantity => quantity > 0 ? `+${quantity}` : quantity < 0 ? `−${-quantity}` : "0"
export const balanceNumber = balance => balance < 0 ? `−${-balance}` : String(balance)
// "27 cases + 14 cans": a quantity in the product's grouping packages, when it makes at least one.
export const packagesLabel = (product, quantity) => {
  const units = product?.grouping?.units
  const whole = Math.abs(quantity)
  return units && whole >= units ? breakdownLabel({ packages: Math.floor(whole / units), units: whole % units }, product.grouping, product.unit) : ""
}
// "420 cans (23 cases + 6 cans)", "−3 pcs".
const amountLabel = (product, quantity) => {
  const packages = packagesLabel(product, quantity)
  const amount = quantityLabel(Math.abs(quantity), product?.unit)
  return `${quantity < 0 ? "−" : ""}${amount}${packages ? ` (${packages})` : ""}`
}

// "Medals Silver 2025 (inactive)": a product as the selects offer it.
export const productName = product => `${product.name}${product.active ? "" : " (inactive)"}`
// "Medals Silver · Champion", "Balls Wilson Tour Premier (can of 4)".
export const placedName = (name, place) => [name, place && placeName(place)].filter(Boolean).join(" · ")
// "Balance now: 420 cans (23 cases + 6 cans)" or, per place, "Balance now: Champion 4 · Finalist 0".
export const balanceLabel = product => `Balance now: ${product.places
  ? product.balances.map(({ place, balance }) => `${placeName(place)} ${balance}`).join(" · ")
  : amountLabel(product, product.balances[0]?.balance ?? 0)}`
export const balanceOf = (product, place) => product.balances.find(row => row.place === place)?.balance ?? 0

// What a movement did, under its type: its supplier and document, destination, count or the movement it reverses.
export const movementDetails = ({ type, details = {}, reverses }) => {
  if (type === "receipt") return [details.supplier, details.document, details.date && `received ${moment(details.date, "YYYY-MM-DD").format("D MMM YYYY")}`]
    .filter(Boolean).join(" · ")
  if (type === "manualIssue") return destinationLabel(details.destination)
  if (type === "inventory") return `Counted ${details.counted} · balance was ${details.balance}`
  if (type === "initialInventory") return `Counted ${details.counted}`
  if (type === "reversal" && reverses) return `Reverses the ${reversedNames[reverses.type] || typeLabel(reverses.type)} of ${timeLabel(reverses.createdAt)}`
  return ""
}
export const reversedName = type => reversedNames[type] || typeLabel(type)

// ——— The quantities a dialog enters: per place, in the product's unit plus its grouping packages ———

const whole = value => /^\d+$/.test(String(value).trim()) && Number(value) <= QUANTITY_LIMIT
// The places a product's form counts: those it keeps a balance at, one per place or one without; a place a
// change of its rule left stays until it is counted.
export const placesOf = product => product?.balances?.map(({ place }) => place) || product?.places || [null]
// A place's entry: the grouping packages (cases) and the units (cans, pcs).
const emptyEntry = () => ({ groupings: "", quantity: "" })
export const entriesFor = product => Object.fromEntries(placesOf(product).map(place => [place ?? "all", emptyEntry()]))
const entryKey = place => place ?? "all"
export const entryOf = (entries, place) => entries[entryKey(place)] || emptyEntry()
const filled = entry => String(entry.groupings).trim() !== "" || String(entry.quantity).trim() !== ""
// An entry's total in the product's unit, or null when it is empty or not whole numbers.
export const totalOf = (product, entry) => {
  if (!filled(entry)) return null
  const [groupings, quantity] = [String(entry.groupings).trim() || "0", String(entry.quantity).trim() || "0"]
  if (!whole(groupings) || !whole(quantity)) return null
  return Number(groupings) * (product?.grouping?.units || 0) + Number(quantity)
}
// The items a command sends for the filled places; the grouping packages only when entered.
export const itemsOf = (product, entries, withProduct = false) => placesOf(product).filter(place => filled(entryOf(entries, place))).map(place => {
  const entry = entryOf(entries, place)
  const groupings = String(entry.groupings).trim()
  return { ...(withProduct ? { productId: product.id } : {}), place, ...(groupings ? { groupings: Number(groupings) } : {}),
    quantity: Number(String(entry.quantity).trim() || "0") }
})
// The errors of the entries, by place: a count needs every place it lists (`every`), a receipt or an issue at
// least one quantity above zero.
export const entryErrors = (product, entries, { every = false, positive = false } = {}) => {
  const errors = {}
  placesOf(product).forEach(place => {
    const entry = entryOf(entries, place)
    if (!filled(entry)) {
      if (every) errors[entryKey(place)] = "Enter the count, 0 included."
      return
    }
    const total = totalOf(product, entry)
    if (total == null || total > QUANTITY_LIMIT) errors[entryKey(place)] = "Enter whole numbers."
    else if (positive && total === 0) errors[entryKey(place)] = "Enter a quantity above 0, or leave it empty."
  })
  if (!every && !Object.keys(errors).length && !placesOf(product).some(place => filled(entryOf(entries, place)))) errors.none = true
  return errors
}
export { entryKey }
